// 試合盤面の zone 表示プリミティブ (= PlayerMat.tsx で使う pile / wide / placeholder 系)。
// tiles.tsx が 300 LOC に近いため、 新 zone 群をここに分離 (ADR-0004 §300 LOC 制約)。

import type { CardView } from "@/bindings/CardView";
import type { ChargeView } from "@/bindings/ChargeView";
import type { SetZoneView } from "@/bindings/SetZoneView";
import { CardTile } from "./tiles";

// landscape 帯レイアウト用: 小型スロット (2×2 クラスタに並べる)。
// label: ゾーン名、count: 枚数 (undefined ならプレースホルダ「未実装」表示)。
export function MiniZone(props: {
  label: string;
  count?: number | undefined;
  topCardName?: string | null | undefined;
  highlight?: boolean | undefined;
}): JSX.Element {
  const { label, count, topCardName, highlight } = props;
  const isPlaceholder = count === undefined;
  const borderCls = highlight === true
    ? "border-emerald-600"
    : "border-slate-600";
  return (
    <div
      className={`flex h-7 w-14 flex-col items-center justify-center rounded border ${borderCls} bg-slate-800 px-0.5 text-center`}
    >
      <span className="text-[8px] font-semibold leading-none text-slate-400">
        {label}
      </span>
      {isPlaceholder ? (
        <span className="text-[7px] leading-none text-slate-700">未実装</span>
      ) : (
        <>
          <span
            className={`text-[9px] font-bold leading-none ${highlight === true ? "text-emerald-300" : "text-slate-200"}`}
          >
            {count}
          </span>
          {topCardName != null && (
            <span className="line-clamp-1 max-w-full text-[6px] leading-none text-slate-500">
              {topCardName}
            </span>
          )}
        </>
      )}
    </div>
  );
}

// SET / 場ｶｰﾄﾞ 用: 上限1枚なので通常カードサイズ (h-16 w-12) のスロット。
// placeholder=true で engine 未実装 (空の破線枠)。card あり/faceDown で CardTile 表示、
// 無ければラベル付き空スロット。
export function CardSlot(props: {
  label: string;
  card?: CardView | null | undefined;
  faceDown?: boolean | undefined;
  placeholder?: boolean | undefined;
}): JSX.Element {
  const { label, card, faceDown, placeholder } = props;
  if (placeholder === true) {
    return (
      <div className="flex h-16 w-12 flex-col items-center justify-center rounded border border-dashed border-slate-700 text-center">
        <span className="text-[8px] text-slate-500">{label}</span>
        <span className="text-[7px] text-slate-700">未実装</span>
      </div>
    );
  }
  if (card != null || faceDown === true) {
    return <CardTile card={card} faceDown={faceDown} />;
  }
  return (
    <div className="flex h-16 w-12 items-center justify-center rounded border border-dashed border-slate-700 text-[8px] text-slate-500">
      {label}
    </div>
  );
}

// DECK 等のカードの塊用: カードサイズ (h-16 w-12) の裏面スタック面 (ラベル + 枚数)。
export function CardPile(props: { label: string; count: number }): JSX.Element {
  const { label, count } = props;
  return (
    <div className="flex h-16 w-12 flex-col items-center justify-center gap-0.5 rounded border border-slate-600 bg-slate-700 text-center">
      <span className="text-[9px] font-semibold text-slate-400">{label}</span>
      <span className="text-sm font-bold text-slate-200">{count}</span>
    </div>
  );
}

// deck / grave / banished 用: ラベル + 枚数 + 先頭カード名 (公開時)。
export function PileZone(props: {
  label: string;
  count: number;
  topCard?: CardView | null | undefined;
}): JSX.Element {
  const { label, count, topCard } = props;
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 rounded border border-slate-600 bg-slate-800 px-1 py-1 text-center text-[10px] text-slate-300">
      <span className="font-semibold text-slate-400">{label}</span>
      <span>{count}</span>
      {topCard?.name != null && (
        <span className="line-clamp-2 px-0.5 text-slate-400">{topCard.name}</span>
      )}
      {count === 0 && <span className="text-slate-600">—</span>}
    </div>
  );
}

// charge zone / installation 用: 横長バー、 cards を横並び (ChargeView / SetZoneView 共用)。
export function WideZone(props: {
  label: string;
  view: ChargeView | SetZoneView;
}): JSX.Element {
  const { label, view } = props;
  const cards =
    view.cards ??
    Array.from({ length: view.count }, () => null as CardView | null);
  return (
    <div className="flex h-full min-h-[4rem] w-full flex-col gap-0.5 rounded border border-slate-600 bg-slate-800 px-2 py-0.5">
      <span className="text-[10px] text-slate-400">
        {label} ({view.count})
      </span>
      <div className="flex flex-1 flex-wrap gap-1 overflow-hidden">
        {cards.length === 0 ? (
          <span className="self-center text-[10px] text-slate-600">—</span>
        ) : (
          cards.map((card, i) => (
            <CardTile
              key={card?.instance_id ?? `wide-hidden-${String(i)}`}
              card={card}
              faceDown={card === null}
            />
          ))
        )}
      </div>
    </div>
  );
}

// field_card / installation / dimension_deck: 未実装プレースホルダ。
// wide=true で横長 (= installation 用)。
export function PlaceholderZone(props: {
  label: string;
  wide?: boolean | undefined;
}): JSX.Element {
  const { label, wide } = props;
  return (
    <div
      className={`flex ${wide === true ? "min-h-[3rem] w-full" : "h-full"} flex-col items-center justify-center gap-0 rounded border border-dashed border-slate-700 bg-transparent text-center`}
    >
      <span className="text-[11px] text-slate-500">{label}</span>
      <span className="text-[9px] text-slate-700">未実装</span>
    </div>
  );
}
